import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Grid, Container, useMediaQuery } from '@material-ui/core';
import { DropDownBtnHLPure, SimpleLoading, GridSelectionHLPure, DialogDetailsPure, SearchBarHLPure, DialogSystemGLPure } from '../../pures';
import './digital-vault-tracking.css'
import { filterBy, orderBy } from '@progress/kendo-data-query'
import { DigitalVaultDocumentAction } from '../../actions';
import { useTranslation } from 'react-i18next';
import { GridNoRecords, Grid as KendoGrid, GridColumn as KendoGridColumn } from '@progress/kendo-react-grid'
import { DateTime } from 'luxon'
import { LocalStorageService } from '../../../services';

const initialSort = [
    {
        field: "date",
        dir: "desc",
    },
];

const DigitalVaultTrackingView = (props) => {
    const {
        selectedCompanyId,
        setSelectedCompanyId,
        documentLogsFiltered,
        isLoading,
        companies,
        selectedLog,
        setSelectedLog,
        searchInBackend,
        isSearchingBackend,
        loadMoreData,
        hasMoreData,
        isLoadingMore,
        onRefreshData
    } = props
    const { t } = useTranslation()
    // Breakpoints responsivos para ocultar columnas progresivamente
    // Orden: se oculta primero la 2, luego la 3 y por último la 4
    const hideCol2 = useMediaQuery('(max-width:1080px)')
    const hideCol3 = useMediaQuery('(max-width:900px)')
    const hideCol4 = useMediaQuery('(max-width:720px)')

    const [documentLogs, setDocumentLogs] = useState([])
    const [displayedLogs, setDisplayedLogs] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const [customFilters, setCustomFilters] = useState([]);
    const [sort, setSort] = useState(initialSort);
    const [skip, setSkip] = useState(0);
    // Controla que solo una fila detalle esté expandida a la vez
    const [expandedId, setExpandedId] = useState(null);

    // Ref to prevent multiple calls to loadMoreData
    const isLoadingRef = useRef(false)

    // Header filters for main grid
    const [documentSearch, setDocumentSearch] = useState('')
    const [dateRangeKey, setDateRangeKey] = useState('') // '' means all
    const hideDateColumn = useMediaQuery('(max-width:1450px)');

    const language = (navigator.languages && navigator.languages[0]) || navigator.language || 'es'


    // Date range options for header dropdown (with translation fallbacks)
    const dateRangeOptions = useMemo(() => ([
        { id: '', name: t('digitalVaultTrackingView.dateRange.all', { defaultValue: 'Todos' }) },
        { id: 'today', name: t('digitalVaultTrackingView.dateRange.today', { defaultValue: 'Hoy' }) },
        { id: 'yesterday', name: t('digitalVaultTrackingView.dateRange.yesterday', { defaultValue: 'Ayer' }) },
        { id: 'last7', name: t('digitalVaultTrackingView.dateRange.last7', { defaultValue: 'Últimos 7 días' }) },
        { id: 'last30', name: t('digitalVaultTrackingView.dateRange.last30', { defaultValue: 'Últimos 30 días' }) },
        { id: 'thisMonth', name: t('digitalVaultTrackingView.dateRange.thisMonth', { defaultValue: 'Este mes' }) },
        { id: 'thisYear', name: t('digitalVaultTrackingView.dateRange.thisYear', { defaultValue: 'Este año' }) },
        { id: 'beforeThisYear', name: t('digitalVaultTrackingView.dateRange.beforeThisYear', { defaultValue: 'Antes de este año' }) },
    ]), [t])

    // Compute start/end boundaries for a given range key
    const getDateRangeBoundaries = (key) => {
        const now = DateTime.now().setLocale(language)
        switch (key) {
            case 'today': {
                const start = now.startOf('day').toJSDate()
                const end = now.endOf('day').toJSDate()
                return { start, end }
            }
            case 'yesterday': {
                const y = now.minus({ days: 1 })
                const start = y.startOf('day').toJSDate()
                const end = y.endOf('day').toJSDate()
                return { start, end }
            }
            case 'last7': {
                const start = now.minus({ days: 6 }).startOf('day').toJSDate()
                const end = now.toJSDate()
                return { start, end }
            }
            case 'last30': {
                const start = now.minus({ days: 29 }).startOf('day').toJSDate()
                const end = now.toJSDate()
                return { start, end }
            }
            case 'thisMonth': {
                const start = now.startOf('month').toJSDate()
                const end = now.toJSDate()
                return { start, end }
            }
            case 'thisYear': {
                const start = now.startOf('year').toJSDate()
                const end = now.toJSDate()
                return { start, end }
            }
            case 'beforeThisYear': {
                const end = now.startOf('year').minus({ millisecond: 1 }).toJSDate()
                return { start: Number.NEGATIVE_INFINITY, end }
            }
            default:
                return null
        }
    }

    const showArrowBtn = useMediaQuery('(max-width: 600px)');
    const showMobile = useMediaQuery('(max-width: 560px)');

    useEffect(() => {
        let filters = []
        if (selectedCompanyId != "0") {
            filters.push({
                field: "companyId",
                operator: "eq",
                value: selectedCompanyId,
            })
        }
        setCustomFilters(filters)
    }, [selectedCompanyId]);

    useEffect(() => {
        let completeFilter = {
            logic: "and",
            filters: [],
        }
        completeFilter.filters = customFilters
        // Base filter (company, etc.)
        let tmp = filterBy(documentLogs, completeFilter)


        // Date range filter over item.date (number ms or date-like string)
        if (dateRangeKey) {
            const bounds = getDateRangeBoundaries(dateRangeKey)
            if (bounds) {
                const { start, end } = bounds
                tmp = tmp.filter((item) => {
                    const itemDate = DateTime.fromISO(item.date).toJSDate();
                    return (itemDate >= start && itemDate <= end)
                })
            }
        }
        const sorted = orderBy(tmp, sort)
        // Marcar expandida solo la fila cuyo id coincide con expandedId
        const withExpand = sorted.map((it) => ({
            ...it,
            expanded: it?.id === expandedId
        }))
        setDisplayedLogs(withExpand)
    }, [documentLogs, customFilters, sort, documentSearch, dateRangeKey, expandedId])

    useEffect(() => {
        setDocumentLogs(documentLogsFiltered)
    }, [documentLogsFiltered])

    // Detect when the user is near the end of the loaded data
    useEffect(() => {
        // Do nothing if we don't have data yet
        if (displayedLogs.length === 0) {
            return
        }

        if (!loadMoreData || !hasMoreData || isLoadingMore || isLoadingRef.current) {
            return
        }

        const totalLoaded = documentLogs.length
        const currentPage = Math.floor(skip / 6) + 1
        const totalPages = Math.ceil(displayedLogs.length / 6)
        const pagesFromEnd = totalPages - currentPage

        // Load more data when we are 1 page from the end
        if (pagesFromEnd <= 1 && totalLoaded > 0) {
            isLoadingRef.current = true
            loadMoreData().finally(() => {
                isLoadingRef.current = false
            })
        }
    }, [skip, displayedLogs.length, hasMoreData])

    const onCompanyDropdownChange = (e) => {
        setSelectedCompanyId(e.target.value)
    }

    const cellColumnOne = (props) => {
        return (
            <td
                style={props.style} // this applies styles that lock the column at a specific position
                className={props.className} // this adds classes needed for locked columns
                colSpan={props.colSpan}
                role={"gridcell"}
                aria-colindex={props.ariaColumnIndex}
                aria-selected={props.isSelected}
            >
                <span className="grid-col1-titles-margin-left ">
                    {props.dataItem.name}
                </span>
            </td>
        );
    };

    const cellColumnTwo = (props) => {
        return (
            <td className=''>
                <span className="grid-cell-subtitles">
                    {props.dataItem.dateValue}
                </span>
            </td>
        );
    };

    const cellColumnThree = (props) => {
        return (
            <td className=''>
                <span className="grid-cell-subtitles">
                    {props.dataItem.userName}
                </span>
            </td>
        );
    };

    // Mantener foco estable: componente memoizado con estado local y propagación con debounce
    const DocumentHeaderCell = React.useMemo(() => {
        return function DocumentHeaderCell() {
            const [localSearch, setLocalSearch] = useState(documentSearch)
            // Propagate to the parent state with debounce for backend search
            useEffect(() => {
                const id = setTimeout(() => {
                    if (localSearch !== documentSearch) {
                        setDocumentSearch(localSearch)
                        // If there is text, search in backend; if empty, reload all
                        if (localSearch && localSearch.trim() !== '') {
                            searchInBackend(localSearch)
                        } else {
                            searchInBackend('')
                        }
                    }
                }, 500)
                return () => clearTimeout(id)
            }, [localSearch])

            return (
                <div className='digitalVaultTracking-wrap'>
                    <span className='digitalVaultTracking-header'>{t('digitalVaultTrackingView.grid.title2')}</span>
                    <SearchBarHLPure
                        typeInput="text"
                        searchValue={localSearch}
                        onSearchChange={(e) => setLocalSearch(e.target.value)}
                    />
                </div>
            )
        }
    }, [t, documentSearch, searchInBackend])

    const DateHeaderCell = () => (
        <div className='digitalVaultTracking-wrap'>
            <span className='digitalVaultTracking-header'>{t('digitalVaultTrackingView.grid.title1')}</span>
            <DropDownBtnHLPure
                optionsDropDown={dateRangeOptions}
                onDropDownChange={(e) => setDateRangeKey(e.target.value)}
                optionsValue={dateRangeKey}
                valueItem={'id'}
                nameOption={'name'}
            />
        </div>
    )

    const cellColumnFour = (props) => {
        return (
            <td className=''>
                <span className="grid-cell-subtitles">
                    {props.dataItem.lastEvent}
                </span>
            </td>
        );
    };

    const onClickFifthAction = (dataItem) => {
        if (dataItem && !dataItem.id) return;
        // Reemplaza el contenido y asegura que el diálogo quede abierto
        setSelectedDocument(JSON.parse(JSON.stringify(dataItem)));
        setModalOpen(true);
    }

    const onCloseDialogDetails = () => {
        setModalOpen(false);
        setSelectedDocument(null);
    }

    // Detail row renderer for subgrid of logs
    const LogDetailRow = (rowProps) => {
        const language = LocalStorageService.retrieveItem('preferredLanguage');
        /** @type {import('../../../types/document-logs.types').DocumentLog[]} */
        const logs = rowProps?.dataItem?.logs || [];
        const [subSort, setSubSort] = useState([{ field: 'dateValue', dir: 'desc' }]);
        const [userFilter, setUserFilter] = useState('');
        const [eventFilter, setEventFilter] = useState(0);

        // Map logs for display
        const raw = useMemo(() => logs.map((log) => {
            const id = log.id;
            const date = DateTime.fromFormat(log.date, 'yyyy-MM-dd HH:mm:ss', { locale: language }).toFormat('dd/MMM/yyyy HH:mm:ss').toLowerCase();
            const dateValue = DateTime.fromFormat(log.date, 'yyyy-MM-dd HH:mm:ss').toJSDate().getTime();
            const userEmail = log?.user?.email || log.userId;
            const eventName = t(`digitalVaultStatisticalReportView.key.${log?.documentAction?.name || ''}`, {
                defaultValue: log?.documentAction?.name || ''
            });
            const eventId = log?.documentAction?.id || 0;

            return ({
                id,
                date,
                dateValue,
                userEmail,
                eventName,
                eventId
            })
        }), [logs, language]);

        const data = useMemo(() => {
            let tmp = raw;
            if (userFilter) tmp = tmp.filter(x => x.userEmail === userFilter);
            if (eventFilter && Number(eventFilter) !== 0) tmp = tmp.filter(x => String(x.eventId) === String(eventFilter));
            return orderBy(tmp, subSort);
        }, [raw, userFilter, eventFilter, subSort]);

        const headerWrap = { display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0 6px 0' };
        const titleStyle = { color: '#123513', fontSize: 14, fontFamily: 'Quicksand, sans-serif' };

        const UserHeaderCell = () => (
            <div style={headerWrap}>
                <span style={titleStyle}>{t('digitalVaultTrackingView.grid.title3')}</span>
                <DropDownBtnHLPure
                    optionsDropDown={[{ id: '', name: t('digitalVaultTrackingView.combobox.allUsers', { defaultValue: 'Todos' }) }, ...rowProps.dataItem.allUsers]}
                    onDropDownChange={(e) => setUserFilter(e.target.value)}
                    optionsValue={userFilter}
                    valueItem={'id'}
                    nameOption={'name'}
                />
            </div>
        );

        const UserAndDateCell = () => (
            <div style={headerWrap}>
                <span style={titleStyle}>{t('digitalVaultTrackingView.grid.dateUser')}</span>
                <DropDownBtnHLPure
                    optionsDropDown={[{ id: '', name: t('digitalVaultTrackingView.combobox.allUsers', { defaultValue: 'Todos' }) }, ...rowProps.dataItem.allUsers]}
                    onDropDownChange={(e) => setUserFilter(e.target.value)}
                    optionsValue={userFilter}
                    valueItem={'id'}
                    nameOption={'name'}
                />
            </div>
        );

        const EventHeaderCell = () => (
            <div style={headerWrap}>
                <span style={titleStyle}>{t('digitalVaultTrackingView.grid.title4')}</span>
                <DropDownBtnHLPure
                    optionsDropDown={[{ id: 0, name: t('digitalVaultTrackingView.combobox.allEvents', { defaultValue: 'Todos' }) }, ...rowProps.dataItem.allEvents]}
                    onDropDownChange={(e) => setEventFilter(e.target.value)}
                    optionsValue={eventFilter}
                    valueItem={'id'}
                    nameOption={'name'}
                />
            </div>
        );

        return (
            <KendoGrid
                data={data}
                sortable
                sort={subSort}
                onSortChange={(e) => setSubSort(e.sort)}
                resizable
                style={{ border: '1px solid #E0E0E0', borderRadius: 6, tableLayout: 'fixed' }}
            >
                {hideDateColumn ?
                    <KendoGridColumn
                        width={'50%'}
                        headerCell={UserAndDateCell}
                        cell={(p) =>
                            <td className='digitalVaultTracking-subgrid'>
                                <span>
                                    {p.dataItem.date}
                                </span>
                                <span>
                                    {p.dataItem.userEmail}
                                </span>
                            </td>
                        }
                    />
                    :
                    <KendoGridColumn field="dateValue" title={t('digitalVaultTrackingView.grid.title1')} width={'33%'} cell={(p) => <td>{p.dataItem.date}</td>} />
                }
                {!hideDateColumn && <KendoGridColumn field="userEmail" headerCell={UserHeaderCell} sortable={false} width={'33%'} />}
                <KendoGridColumn field="eventName" headerCell={EventHeaderCell} sortable={false} width={hideDateColumn ? '50%' : '33%'} />
                <GridNoRecords>
                    <span style={{
                        color: "#2C353C",
                        fontSize: "16px",
                        fontWeight: 550,
                        fontFamily: "Quicksand"
                    }}>
                        {t("configurationDomainPanel.notResults")}
                    </span>
                </GridNoRecords>
            </KendoGrid>
        );
    };

    const document = [
        {
            key: 'name',
            label: t('fileManager.detailsDialog.labels.documentName'),
        },
        {
            key: 'userName',
            label: t('fileManager.detailsDialog.labels.owner'),
        },
        {
            line: true
        },
        {
            key: 'startDate',
            label: t('fileManager.detailsDialog.labels.startDate'),
        },
        {
            key: 'renewal',
            label: t('fileManager.detailsDialog.labels.expirationDate'),
        },
        {
            line: true
        },
        {
            key: 'typeDocument',
            label: t('fileManager.detailsDialog.labels.typeDocument'),
        },
        {
            key: 'confidential',
            label: t('fileManager.detailsDialog.labels.confidential'),
        },
        {
            line: true
        },
        {
            key: 'description',
            label: t('fileManager.detailsDialog.labels.description'),
        },
        {
            key: 'summary',
            label: t('fileManager.detailsDialog.labels.summary.ia'),
        },
        {
            line: true
        }
    ]

    function getDetails({ obj }) {
        const data = document.map(({ key, label, line }) => {
            let value = obj[key]
            if (label === t('fileManager.detailsDialog.labels.confidential')) value = obj[key] ? t('fileManager.detailsDialog.labels.confidential.yes') : t('fileManager.detailsDialog.labels.confidential.no')
            const data = {
                detail: label,
                value,
                line
            }
            return data
        })

        return data
    }

    const ChildrenDialogDetails = () => {
        const final = getDetails({ obj: selectedDocument })
        return (
            <div className='syncfusionFileManager-dialog-details'>
                <ul>
                    {final.map((detail, index) => {
                        if (detail.line) {
                            return <hr key={index} className='syncfusionFileManager-hr' />
                        } else {
                            return (
                                <li key={index}>
                                    <span className='syncfusionFileManager-labelKey'>{detail.detail}:</span>{' '}
                                    <span className='syncfusionFileManager-labelValue'>{detail.value}</span>
                                </li>
                            )
                        }
                    })}
                </ul>
            </div>
        )
    }

    const childrenDialog = <ChildrenDialogDetails />

    if (selectedLog) {
        return <DigitalVaultDocumentAction
            documentId={selectedLog.id}
            goBack={async () => {
                const index = displayedLogs.findIndex(documentLog => documentLog.id == selectedLog.id);
                setSkip(Math.floor(index / 6) * 6);
                setSelectedLog(null);

                // Refrescar datos cuando regresa de ver el documento
                if (onRefreshData) {
                    console.log('Refrescando datos después de ver documento...')
                    await onRefreshData()
                }
            }}
            stateToMaintain={{ selectedLog, selectedCompanyId }}
        />
    }

    return (
        <Container
            disableGutters
            className="digitalVaultTracking-container"
        >
            {isLoading ? (
                <SimpleLoading />
            ) : (
                <>
                    <Grid
                        container
                        className="digitalVaultCategoriesAdministration-body"
                    >
                        <Grid
                            item
                            xs={12}
                            className="digitalVaultTracking-navbar"
                        >
                            <Grid
                                item
                                lg={3}
                                md={4}
                                xs={12}
                                sm={12}
                            >
                                <DropDownBtnHLPure
                                    optionsDropDown={companies}
                                    optionsValue={selectedCompanyId}
                                    valueItem={"id"}
                                    nameOption={"companyName"}
                                    onDropDownChange={onCompanyDropdownChange}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            className="digitalVaultCategoriesAdministration-gridContainer"
                        >
                            <GridSelectionHLPure
                                {...props}
                                dataGrid={displayedLogs}
                                skip={skip}
                                take={6}
                                preservePageOnDataChange
                                styleGrid={{ tableLayout: 'fixed' }}
                                onPageChangeCallback={(page) => {
                                    setSkip(page.skip)
                                }}
                                // Subgrid detail configuration
                                detail={LogDetailRow}
                                expandField={'expanded'}
                                onExpandChange={(e) => {
                                    // Si ya estaba expandida, colapsa; si no, expande solo esta
                                    const isCurrentlyExpanded = !!e?.dataItem?.expanded
                                    setExpandedId(isCurrentlyExpanded ? null : e?.dataItem?.id)
                                }}
                                titleRow1={t("digitalVaultTrackingView.grid.title2")}
                                headerColumnOne={DocumentHeaderCell}
                                filterableColumn1={false}
                                cellColumnOne={cellColumnOne}
                                isCheckedColOne={'date'}
                                widthColumnOne={showMobile ? "175px" : "250px"}
                                //2
                                columnTwo={!hideCol2}
                                titleRow2={t("digitalVaultTrackingView.grid.title1")}
                                headerColumnTwo={DateHeaderCell}
                                fieldColumn2={"documentName"}
                                cellColumnTwo={cellColumnTwo}
                                widthColumnTwo={200}
                                //3
                                columnThree={!hideCol3}
                                titleRow3={t("digitalVaultTrackingView.grid.title3")}
                                fieldColumn3={"userName"}
                                cellColumnThree={cellColumnThree}
                                headerColumnThreeClass={"digitalVaultTrackingView-header-col3"}
                                widthColumnThree={200}
                                //4
                                columnFour={!hideCol4}
                                titleRow4={t("digitalVaultTrackingView.grid.lastEvent")}
                                fieldColumn4={"eventName"}
                                cellColumnFour={cellColumnFour}
                                widthColumnFour={200}
                                headerColumnFourClass={"digitalVaultTrackingView-header-col4"}
                                //5
                                columnFive
                                lockedColumnFive={true}
                                widthColumnFive={showArrowBtn ? 35 : 200}
                                titleRow5={showArrowBtn ? "" : t("digitalVaultTrackingView.grid.title5")}
                                headerColumnFiveClass={"digitalVaultTrackingView-header-col5"}
                                columnFiveStyleClass={"digitalVaultTrackingView-cell-col5"}
                                filterableColumn5={false}
                                labelOneActions={t("button.detail")}
                                iconOneActive
                                activeDialog1
                                mobileFunction
                                iconFifthActive
                                iconFifth
                                iconSrc="https://a3cloud-development.s3.us-west-2.amazonaws.com/microservices/MiFirma/assets/boveda-digital/fileManager/icon_detail_disabled.svg"
                                labelFifthActions
                                onFifthActionClick={onClickFifthAction}
                                btnOpenDialog1={(dataItem) => setSelectedLog(dataItem)}
                                onSelectedItem={onClickFifthAction}
                                setNameComponentUser={() => { }}
                                setShowDetails={() => { }}
                            />
                        </Grid>
                    </Grid>
                </>
            )}


            {modalOpen && (
                <DialogDetailsPure
                    children={childrenDialog}
                    dialogTitle={t('fileManager.detailsDialog.labels.detailsTitle')}
                    showBtnSecondary={true}
                    setCloseDialog={onCloseDialogDetails}
                />
            )}

            {isLoadingMore && (
                <DialogSystemGLPure
                    showLoadingRequesting={true}
                    toggleLoadingRequesting={() => { }}
                />
            )}

            {isSearchingBackend && (
                <DialogSystemGLPure
                    showLoadingRequesting={true}
                    toggleLoadingRequesting={() => { }}
                />
            )}
        </Container>

    );

}

export default DigitalVaultTrackingView
